// EOSK.js — EOS-based K-values with per-tray EOS selection.
import { solvePR } from "./eos/PR";
import { solvePRSV } from "./eos/PRSV";
import { solveSRK } from "./eos/SRK";

const R = 8.314462618;

// ---------- EOS choice per tray (kept for compatibility) ----------
export function getEOSForTray(trayIndex, trays, crudeHint, eosMode, eosManual) {
  // Manual override (UI-controlled). If eosMode === "manual", force eosManual on all trays.
  if (eosMode === "manual" && eosManual) return eosManual;

  // simple heuristic: top → SRK, middle → PR, bottom → PRSV
  const a = trayIndex / Math.max(1, trays - 1);
  if (a > 0.66) return "SRK"; // light ends near the top
  if (a > 0.33) return "PR"; // mid column
  return "PRSV"; // heavy ends near the bottom
}

// ---------- Internal: call the right solver and normalize outputs ----------
function solveByEOS(eos, { P, T, x, comps, kij }) {
  // We keep a common shape { ZL, ZV, phiL, phiV, hdepL, hdepV, ... }
  if (eos === "PRSV") return solvePRSV({ P, T, x, comps, kij });
  if (eos === "PR") return solvePR({ P, T, x, comps, kij });
  if (eos === "SRK") return solveSRK({ P, T, x, comps, kij });
  // Anything else → use PR as a stable default
  return solvePR({ P, T, x, comps, kij });
}

// ---------- Public: φ–φ K-values ----------
let LOG_EOSK = false; // <— new global switch

// PHFlash calls eosK many times during temperature bracketing/root-finding.
// Once the EOS values stabilize, this can generate long runs of identical
// [EOSK] lines that hide useful signal. This cache suppresses consecutive
// duplicates without changing any calculations (logging-only behavior).
const __eoskLogState = new Map();

export function setEosKLogging(on) {
  LOG_EOSK = !!on;
} // optional helper

export function eosK(
  P,
  T,
  z,
  comps,
  trayIndex = 0,
  trays = 32,
  crudeHint,
  kij = null,
  log = false,
  murphreeEtaV = null,
  eosMode = "auto",
  eosManual = "PRSV"
) {
  const eos = getEOSForTray(trayIndex, trays, crudeHint, eosMode, eosManual);

  // ---- φ–φ K-value iteration using phase compositions (x/y) ----
  // NOTE: solveByEOS(eos, {x}) returns both liquid-root and vapor-root fugacity
  // coeffs for the *given* composition. For φ–φ we need φ^L(x) and φ^V(y),
  // so we call the EOS twice: once at x (use phiL), once at y (use phiV).
  const n = Array.isArray(z) ? z.length : 0;
  if (!n) return { K: [], Z_liq: NaN, Z_vap: NaN, eos };

  // normalize feed composition defensively
  const zsum = z.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
  const zn = z.map((v) => (Number.isFinite(v) && zsum > 0 ? v / zsum : 1 / n));

  const clampK = (kv) =>
    Math.max(1e-6, Math.min(1e6, Number.isFinite(kv) && kv > 0 ? kv : 1.0));

  // Optional Murphree-style efficiency applied to K-values.
  // This is a simplified, robust approximation used in many educational/engineering column models:
  //   K_eff = 1 + eta * (K_eq - 1)
  // It reduces separation strength continuously toward K=1 as eta→0, while preserving the sign around 1.
  const etaK = Number.isFinite(murphreeEtaV) ? Math.max(0, Math.min(1, murphreeEtaV)) : null;
  const applyEtaToK = (kEq) => {
    if (etaK === null) return kEq;
    return 1 + etaK * (kEq - 1);
  };

  // local Rachford–Rice (bisection) to avoid circular deps
  function rrV(zv, Kv) {
    const f = (V) => {
      let s = 0;
      for (let i = 0; i < zv.length; i++) {
        const Ki = Kv[i];
        const denom = 1 + V * (Ki - 1);
        s += zv[i] * (Ki - 1) / Math.max(1e-16, denom);
      }
      return s;
    };
    const f0 = f(0);
    const f1 = f(1);
    // no root in [0,1] → single-phase
    if (f0 > 0 && f1 > 0) return 1.0;
    if (f0 < 0 && f1 < 0) return 0.0;

    let lo = 0,
      hi = 1,
      flo = f0,
      fhi = f1;
    for (let it = 0; it < 60; it++) {
      const mid = 0.5 * (lo + hi);
      const fm = f(mid);
      if (Math.abs(fm) < 1e-12) return mid;
      if (flo * fm <= 0) {
        hi = mid;
        fhi = fm;
      } else {
        lo = mid;
        flo = fm;
      }
    }
    return 0.5 * (lo + hi);
  }

  // initial K from same-composition φ ratio (cheap but often OK as a start)
  let { ZL: ZL0, ZV: ZV0, phiL: phiL0, phiV: phiV0 } = solveByEOS(eos, {
    P,
    T,
    x: zn.slice(),
    comps,
    kij,
  });
  let lastSolL = null;
  let lastSolV = null;
  let K = phiL0.map((pl, i) => {
    const keq = clampK(pl / Math.max(1e-16, phiV0[i]));
    return clampK(applyEtaToK(keq));
  });

  // iterate a few times to make K consistent with x/y
  const maxIter = 6;
  const damp = 0.5; // under-relax K updates for stability
  let ZL = ZL0,
    ZV = ZV0;

  for (let it = 0; it < maxIter; it++) {
    const V = rrV(zn, K);

    // compute x and y
    let x = new Array(n);
    let y = new Array(n);
    let xsum = 0,
      ysum = 0;
    for (let i = 0; i < n; i++) {
      const denom = 1 + V * (K[i] - 1);
      x[i] = zn[i] / Math.max(1e-16, denom);
      xsum += x[i];
    }
    for (let i = 0; i < n; i++) x[i] = x[i] / Math.max(1e-16, xsum);

    for (let i = 0; i < n; i++) {
      y[i] = K[i] * x[i];
      ysum += y[i];
    }
    for (let i = 0; i < n; i++) y[i] = y[i] / Math.max(1e-16, ysum);

    // evaluate fugacity coefficients at phase compositions
    const solL = solveByEOS(eos, { P, T, x, comps, kij });
    const solV = solveByEOS(eos, { P, T, x: y, comps, kij });
    lastSolL = solL;
    lastSolV = solV;
    // If either EOS evaluation reports single-phase or collapsed roots, stop φ–φ updates.
    if (solL.singlePhase || solV.singlePhase) {
      ZL = solL.Z ?? solL.ZL ?? ZL;
      ZV = solV.Z ?? solV.ZV ?? ZV;
      const Zs = (solL.Z ?? solL.ZL ?? ZL);
      const phase = (Zs >= 0.3) ? "V" : "L";
      return { K: null, Z_liq: Zs, Z_vap: Zs, eos, singlePhase: true, phase, Z: Zs };
    }
    // Treat nearly-equal roots as single-phase (avoid K→1 collapse)
    const relZL = Math.abs((solL.ZV ?? solL.ZL) - (solL.ZL ?? solL.ZV)) / Math.max(1e-12, Math.abs(solL.ZV ?? solL.ZL));
    const relZV = Math.abs((solV.ZV ?? solV.ZL) - (solV.ZL ?? solV.ZV)) / Math.max(1e-12, Math.abs(solV.ZV ?? solV.ZL));
    if (relZL < 1e-8 || relZV < 1e-8) {
      ZL = solL.ZL ?? ZL;
      ZV = solV.ZV ?? ZV;
      const Zs = (solL.Z ?? solL.ZL ?? ZL);
      const phase = (Zs >= 0.3) ? "V" : "L";
      return { K: null, Z_liq: Zs, Z_vap: Zs, eos, singlePhase: true, phase, Z: Zs };
    }

    const phiL = solL.phiL;
    const phiV = solV.phiV;

    ZL = solL.ZL ?? ZL;
    ZV = solV.ZV ?? ZV;

    // update K
    let maxRel = 0;
    const Knew = new Array(n);
    for (let i = 0; i < n; i++) {
      const kvEq = clampK(phiL[i] / Math.max(1e-16, phiV[i]));
      const kv = clampK(applyEtaToK(kvEq));
      // damped update
      Knew[i] = clampK((1 - damp) * K[i] + damp * kv);
      const rel = Math.abs(Knew[i] - K[i]) / Math.max(1e-12, K[i]);
      if (rel > maxRel) maxRel = rel;
    }
    K = Knew;

    if (maxRel < 1e-6) break;
  }


// Low-noise diagnostics: only warn in numerically dangerous regions
try {
  const bL = lastSolL?.b_mix ?? lastSolL?.bMix ?? null;
  const bV = lastSolV?.b_mix ?? lastSolV?.bMix ?? null;
  const BL = (Number.isFinite(bL) ? (bL * P) / (R * T) : NaN);
  const BV = (Number.isFinite(bV) ? (bV * P) / (R * T) : NaN);
  const zmbL = (Number.isFinite(ZL) && Number.isFinite(BL)) ? (ZL - BL) : NaN;
  const zmbV = (Number.isFinite(ZV) && Number.isFinite(BV)) ? (ZV - BV) : NaN;

  let kminW = Infinity, kmaxW = -Infinity;
  for (const k of K)
    if (Number.isFinite(k)) {
      if (k < kminW) kminW = k;
      if (k > kmaxW) kmaxW = k;
    }

  const nearSingular = Number.isFinite(zmbL) && zmbL < 1e-6;
  const extremeK = (Number.isFinite(kmaxW) && kmaxW > 1e3) || (Number.isFinite(kminW) && kminW < 1e-3);

  // simple per-tray throttle
  eosK.__warnCounts = eosK.__warnCounts || {};
  const key = String(trayIndex ?? "?");
  const c = (eosK.__warnCounts[key] || 0);

  if ((nearSingular || extremeK) && c < 5) {
    eosK.__warnCounts[key] = c + 1;
    console.log(
      `[EOSK_WARN] tray=${trayIndex} eos=${eos} T=${T.toFixed(2)} P=${P.toFixed(0)} ` +
        `B_L=${Number.isFinite(BL) ? BL.toExponential(3) : "?"} ` +
        `ZL=${Number.isFinite(ZL) ? ZL.toExponential(3) : "?"} ` +
        `ZV=${Number.isFinite(ZV) ? ZV.toExponential(3) : "?"} ` +
        `ZL-B=${Number.isFinite(zmbL) ? zmbL.toExponential(3) : "?"} ` +
        `Kmin=${Number.isFinite(kminW) ? kminW.toExponential(3) : "?"} ` +
        `Kmax=${Number.isFinite(kmaxW) ? kmaxW.toExponential(3) : "?"}`
    );
  }
} catch {
  // ignore diagnostics failures
}
  if (log || LOG_EOSK) {
    let kmin = Infinity,
      kmax = -Infinity;
    for (const k of K)
      if (Number.isFinite(k)) {
        if (k < kmin) kmin = k;
        if (k > kmax) kmax = k;
      }
    const ZLr = Number.isFinite(ZL) ? ZL.toFixed(4) : String(ZL);
    const ZVr = Number.isFinite(ZV) ? ZV.toFixed(4) : String(ZV);
    const kminr = Number.isFinite(kmin) ? kmin.toExponential(2) : "?";
    const kmaxr = Number.isFinite(kmax) ? kmax.toExponential(2) : "?";
    const msg =
      `[EOSK] tray=${trayIndex} eos=${eos} ZL=${ZLr} ZV=${ZVr} ` +
      `Kmin=${kminr} Kmax=${kmaxr}`;

    // De-duplicate consecutive identical lines per (tray,eos).
    // Still print periodically so you can see long stalls.
    const key = `${trayIndex}|${eos}`;
    const sig = msg; // already contains rounded values
    const prev = __eoskLogState.get(key);
    if (!prev || prev.sig !== sig) {
      __eoskLogState.set(key, { sig, count: 0 });
      console.log(msg);
    } else {
      prev.count += 1;
      // Print every 20th duplicate so you can tell it's still iterating.
      if (prev.count % 20 === 0) {
        console.log(`${msg} (x${prev.count + 1})`);
      }
    }
  }

  return { K, Z_liq: ZL, Z_vap: ZV, eos };
}


// ---------- Public: mixture packages for enthalpy modules ----------
export function solvePRSV_mixture({ P, T, x, comps, kij = null }) {
  return solvePRSV({ P, T, x, comps, kij });
}

export function solvePR_mixture({ P, T, x, comps, kij = null }) {
  return solvePR({ P, T, x, comps, kij });
}